import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { QUALITY_LABELS, type ManualReleaseDTO } from '@jelly-dl/shared';
import { api, ApiError } from '../api/client';
import { Badge, Button, Card, Input, Spinner } from '../components/ui';
import { QUALITY_COLORS, humanBytes } from '../lib/format';

export function Search() {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<ManualReleaseDTO[] | null>(null);
  const [msg, setMsg] = useState('');
  const [grabbed, setGrabbed] = useState<Record<string, string>>({});

  // Raw indexer (scenenzbs) search for the exact text — grab only what you pick.
  const search = useMutation({
    mutationFn: (query: string) => api.post<{ results: ManualReleaseDTO[] }>('/api/search', { q: query }),
    onSuccess: (d) => {
      setResults(d.results);
      setGrabbed({});
      setMsg(d.results.length ? '' : 'No releases found.');
    },
    onError: (e) => {
      setResults(null);
      setMsg(e instanceof ApiError ? e.message : 'Search failed');
    },
  });

  const grab = useMutation({
    mutationFn: (id: string) => api.post('/api/search/grab', { id }),
    onSuccess: (_d, id) => setGrabbed((g) => ({ ...g, [id]: 'sent' })),
    onError: (e, id) => setGrabbed((g) => ({ ...g, [id]: e instanceof ApiError ? e.message : 'failed' })),
  });

  return (
    <div className="space-y-5">
      <h1 className="text-xl font-semibold">Search</h1>
      <p className="text-sm text-slate-400">
        Search the indexer (scenenzbs) directly for any release text, then grab the ones you want. The download is
        queued in NZBGet and imported into your Jellyfin library.
      </p>

      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (q.trim()) search.mutate(q.trim());
        }}
      >
        <Input
          placeholder="e.g. The Matrix 1999 2160p"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          autoFocus
        />
        <Button type="submit" disabled={search.isPending}>
          {search.isPending ? 'Searching…' : 'Search'}
        </Button>
      </form>

      {msg && <p className="text-sm text-amber-400">{msg}</p>}
      {search.isPending && <Spinner label="Searching scenenzbs…" />}

      {results && results.length > 0 && (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="py-1 pr-2">Release</th>
                  <th className="py-1 pr-2">Quality</th>
                  <th className="py-1 pr-2">Size</th>
                  <th className="py-1 pr-2">Age</th>
                  <th className="py-1 pr-2">Grabs</th>
                  <th className="py-1" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {results.map((r) => (
                  <tr key={r.id}>
                    <td className="max-w-md py-1.5 pr-2">
                      <div className="flex items-center gap-2">
                        <span className="truncate text-slate-200" title={r.title}>
                          {r.title}
                        </span>
                        {r.isSeasonPack && <Badge className="bg-indigo-600/20 text-indigo-200">Pack</Badge>}
                      </div>
                      {r.hasPassword && <div className="text-xs text-rose-400">password-protected</div>}
                    </td>
                    <td className="py-1.5 pr-2">
                      <Badge className={QUALITY_COLORS[r.qualityKey]}>{QUALITY_LABELS[r.qualityKey]}</Badge>
                    </td>
                    <td className="py-1.5 pr-2 text-slate-400">{humanBytes(r.sizeBytes)}</td>
                    <td className="py-1.5 pr-2 text-slate-400">{r.ageDays != null ? `${r.ageDays}d` : '—'}</td>
                    <td className="py-1.5 pr-2 text-slate-400">{r.grabs}</td>
                    <td className="py-1.5 text-right">
                      {grabbed[r.id] === 'sent' ? (
                        <span className="text-xs text-emerald-400">queued ✓</span>
                      ) : grabbed[r.id] ? (
                        <span className="text-xs text-rose-400">{grabbed[r.id]}</span>
                      ) : (
                        <Button
                          variant="subtle"
                          disabled={r.hasPassword || (grab.isPending && grab.variables === r.id)}
                          onClick={() => grab.mutate(r.id)}
                        >
                          Grab
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
